/**
 * Tracks and layers (W1-10b core-commands-motion; DESIGN.md §5.6.1, §5.6.3, §6.6, §6.7).
 *
 *  - `ensureTrackBody`: creates a track with one key; for 'extrapolate' properties a first key after beat 0 gets
 *    the beat-0 Stay key with `Defaults.leadInValue` (or the value when there is no exact neutral value), so the
 *    new track never extrapolates backwards (lead-in invariant; `commitKeys` inserts it).
 *  - `addLayerBody`: a new additive layer (lowest free index of 0..3) holding 0 from beat 0 (no effect until
 *    changed); `deleteLayerBody` deletes one layer's track and forgets its name; `resetPropertyBody` deletes the
 *    whole track (the inspector then explains what "absent" means).
 *  - `moveSegmentsToLayerBody`: moves selected segments of summed properties to another layer by swapping the two
 *    layers' curves inside the moved spans (shape-preserving splits at the span edges, as paste does): the target
 *    takes the segments, the source takes what the target had there (0 for a new layer), so the sum over the
 *    layers is unchanged everywhere and no key is lost. A layer whose lead-in Phira extrapolates (imported, first
 *    key after beat 0) gets an explicit lead-in segment from beat 0 first when a span starts at or before its first
 *    key (motionTime.ts `explicitLeadIn`), so the sum before the span is kept too. X/Y-linked lines swap both
 *    position axes.
 *  - `holdLeadInBody`: fix for imported lead-ins (PH-09): a Stay key at beat 0 with the first value; clears
 *    `importedLeadIn`. On X/Y-linked lines the other position axis of the same layer gets a key at beat 0 too (its
 *    own Stay when it also starts after beat 0), so both axes keep their keys at the same beats.
 */

import { HOLD, LINEAR } from '../ease/index';
import { LAYERED_PROPS, PROP_SPECS } from '../model/props';
import type { KeyId, LineId, LineProp, TrackId, TrackOwner, TrackValue } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import { cleanLinks, startsSegment, xyPartnerTrack } from './link';
import {
  commitKeys,
  createTrack,
  deleteTrack,
  groupByTrack,
  MOTION_MSG,
  newRec,
  ownerInfo,
  readKeys,
  reason,
  requireLine,
  requireTrack,
  requireValue,
  type KeyRec,
  type TrackInfo,
} from './motionTrack';
import { collectSplit, notePartnerSplit, noteWorstSplit, type SplitOn } from './motionNotes';
import { replaceSpan, sliceBetween } from './motionTime';
import { curveCtx, EXACT_SPLIT, insertKeyRec, type SplitReport } from './splitKey';

/** RPE shows four eventLayers; the editor creates at most this many. */
export const MAX_UI_LAYERS = 4;

export function ensureTrackBody(tx: TxBuilder, owner: TrackOwner, value: TrackValue, at: Beat = Beats.ZERO): TrackId {
  const existing = tx.idx.trackOfOwner(owner);
  if (existing !== undefined) return existing;
  const info = createTrack(tx, owner);
  const v = requireValue(tx, info, value);
  commitKeys(tx, info, [newRec(at, v, v, LINEAR)]);
  tx.setFocus(owner.kind === 'line' ? { beat: at, line: owner.line } : { beat: at });
  return info.track.id;
}

/** Layer indices in use for a summed property of a line. */
export function layersInUse(tx: TxBuilder, line: LineId, prop: LineProp): number[] {
  const out: number[] = [];
  for (const id of tx.idx.tracksOfLine(line).get(prop) ?? []) {
    const t = tx.doc.tracks.get(id);
    if (t?.owner.kind === 'line') out.push(t.owner.layer);
  }
  return out;
}

/** Lowest layer index (0..3) without a track, or null. */
export function freeLayer(
  tx: TxBuilder,
  line: LineId,
  prop: LineProp,
  taken: ReadonlySet<number> = new Set(),
): number | null {
  const used = new Set(layersInUse(tx, line, prop));
  for (let i = 0; i < MAX_UI_LAYERS; i++) if (!used.has(i) && !taken.has(i)) return i;
  return null;
}

export function addLayerBody(tx: TxBuilder, line: LineId, prop: LineProp): number {
  requireLine(tx, line);
  if (!PROP_SPECS[prop].layered) tx.fail(reason(MOTION_MSG.notLayered));
  const layer = freeLayer(tx, line, prop);
  if (layer === null) tx.fail(reason(MOTION_MSG.layerLimit, { max: MAX_UI_LAYERS }));
  const info = createTrack(tx, { kind: 'line', line, prop, layer });
  commitKeys(tx, info, [newRec(Beats.ZERO, 0, 0, HOLD)]);
  tx.setFocus({ beat: Beats.ZERO, line });
  return layer;
}

function forgetLayerName(tx: TxBuilder, line: LineId, layer: number): void {
  const rec = tx.doc.lines.get(line);
  if (rec === undefined) return;
  const names = rec.editor.layerNames;
  if (layer >= names.length || names[layer] === null) return;
  const next = names.map((n, i) => (i === layer ? null : n));
  while (next.length > 0 && next[next.length - 1] === null) next.pop();
  tx.update('lines', line, { editor: { ...rec.editor, layerNames: next } });
}

export function deleteLayerBody(tx: TxBuilder, line: LineId, prop: LineProp, layer: number): void {
  requireLine(tx, line);
  const id = tx.idx.track(line, prop, layer);
  if (id === undefined) tx.fail(reason(MOTION_MSG.noTrack));
  deleteTrack(tx, requireTrack(tx, id));
  // Layer names belong to the layer index of the line; a deleted summed layer forgets its name only when no
  // other summed property still uses that index.
  if (PROP_SPECS[prop].layered && !LAYERED_PROPS.some((p) => tx.idx.track(line, p, layer) !== undefined)) {
    forgetLayerName(tx, line, layer);
  }
  tx.setFocus({ line });
}

export function resetPropertyBody(tx: TxBuilder, line: LineId, prop: LineProp, layer: number): void {
  requireLine(tx, line);
  const id = tx.idx.track(line, prop, PROP_SPECS[prop].layered ? layer : 0);
  if (id === undefined) tx.fail(reason(MOTION_MSG.noTrack));
  deleteTrack(tx, requireTrack(tx, id));
  tx.setFocus({ line });
}

/** Writes a Stay key at beat 0 holding the first value of a track whose first key is after beat 0. */
function holdFirstValue(tx: TxBuilder, info: TrackInfo, keys: KeyRec[]): void {
  const first = keys[0];
  if (first === undefined || Beats.cmp(first.t, Beats.ZERO) <= 0) return;
  keys.unshift(newRec(Beats.ZERO, first.vOut, first.vOut, HOLD));
  commitKeys(tx, info, keys);
}

/**
 * The X/Y partner of a track that got a key at beat 0 (`holdLeadInBody`): a partner that starts after beat 0 holds
 * its own first value there as well; a partner that starts before beat 0 (imported) without a key at 0 is split
 * there shape-preservingly. Returns the report of that split (exact otherwise).
 */
function partnerKeyAtZero(tx: TxBuilder, info: TrackInfo): SplitReport {
  const partner = xyPartnerTrack(tx, info);
  if (partner === undefined) return EXACT_SPLIT;
  const pInfo = requireTrack(tx, partner);
  const keys = readKeys(tx, partner);
  const first = keys[0];
  if (first === undefined) return EXACT_SPLIT;
  if (Beats.cmp(first.t, Beats.ZERO) > 0) {
    holdFirstValue(tx, pInfo, keys);
    return EXACT_SPLIT;
  }
  const unlinked = new Set<number>();
  const r = insertKeyRec(keys, Beats.ZERO, curveCtx(tx, pInfo), unlinked);
  if (!r.created) return EXACT_SPLIT;
  commitKeys(tx, pInfo, keys);
  cleanLinks(tx, unlinked);
  return r;
}

export function holdLeadInBody(tx: TxBuilder, track: TrackId): void {
  const info = requireTrack(tx, track);
  const keys = readKeys(tx, track);
  const first = keys[0];
  if (first === undefined) tx.fail(reason(MOTION_MSG.noKeys));
  if (Beats.cmp(first.t, Beats.ZERO) <= 0) tx.fail(reason(MOTION_MSG.leadInAlready));
  holdFirstValue(tx, info, keys);
  notePartnerSplit(tx, partnerKeyAtZero(tx, info));
  tx.setFocus(info.line === null ? { beat: Beats.ZERO } : { beat: Beats.ZERO, line: info.line.id });
}

/** Consecutive selected segments of one track as [start index, end index] key ranges. */
function segmentRuns(keys: readonly KeyRec[], selected: ReadonlySet<KeyId>): [number, number][] {
  const runs: [number, number][] = [];
  for (let i = 0; i + 1 < keys.length; i++) {
    const id = (keys[i] as KeyRec).id;
    if (id === null || !selected.has(id)) continue;
    const last = runs[runs.length - 1];
    if (last !== undefined && last[1] === i) last[1] = i + 1;
    else runs.push([i, i + 1]);
  }
  return runs;
}

type Span = readonly [Beat, Beat];

/** Sorted, disjoint union of beat spans (overlapping or touching spans merged). */
function unionSpans(spans: readonly Span[]): Span[] {
  const sorted = [...spans].sort((a, b) => Beats.cmp(a[0], b[0]));
  const out: [Beat, Beat][] = [];
  for (const [s, e] of sorted) {
    const last = out[out.length - 1];
    if (last !== undefined && Beats.lte(s, last[1])) {
      if (Beats.lt(last[1], e)) last[1] = e;
    } else out.push([s, e]);
  }
  return out;
}

/** One property of one line whose layers `from` and `to` swap their curves inside `spans`. */
interface SwapJob {
  readonly line: LineId;
  readonly prop: LineProp;
  readonly from: number;
  spans: Span[];
  /** Segments selected by the user (not mirrored onto the other position axis). */
  selected: number;
}

/** True when the keys hold 0 everywhere (what an absent layer contributes). */
function allZero(keys: readonly KeyRec[]): boolean {
  return keys.every((k) => k.vIn === 0 && k.vOut === 0);
}

/** A layer's working keys: the track's keys, or a constant 0 from beat 0 for an absent layer. */
interface LayerWork {
  readonly owner: Extract<TrackOwner, { kind: 'line' }>;
  readonly info: TrackInfo;
  readonly exists: boolean;
  readonly keys: KeyRec[];
}

function layerWork(tx: TxBuilder, line: LineId, prop: LineProp, layer: number): LayerWork | null {
  const owner = { kind: 'line', line, prop, layer } as const;
  const found = ownerInfo(tx, owner);
  if (found === null) return null;
  const keys = found.exists ? readKeys(tx, found.info.track.id) : [newRec(Beats.ZERO, 0, 0, HOLD)];
  return { owner, info: found.info, exists: found.exists, keys };
}

/** Writes a layer back (creating the track of an absent layer unless it still holds 0 everywhere). */
function commitLayer(tx: TxBuilder, w: LayerWork): void {
  if (w.exists) {
    commitKeys(tx, w.info, w.keys);
    return;
  }
  if (allZero(w.keys)) return;
  const used = layersInUse(tx, w.owner.line, w.owner.prop);
  if (w.owner.layer >= MAX_UI_LAYERS && !used.includes(w.owner.layer)) {
    tx.fail(reason(MOTION_MSG.layerLimit, { max: MAX_UI_LAYERS }));
  }
  commitKeys(tx, createTrack(tx, w.owner), w.keys);
}

/**
 * Moves selected segments of summed properties (position, rotation, opacity, scroll speed) to another layer by
 * SWAPPING the two layers' curves inside each moved span: the target layer takes the segments, the source layer
 * takes what the target layer had there (0 for an absent or empty layer). The sum over the layers is therefore
 * unchanged everywhere, and keys the target layer had inside the span are kept (on the source layer). Span edges
 * inside a segment of either layer are split shape-preservingly (approximations are noted). A layer whose lead-in
 * is extrapolated (imported, first key after beat 0) gets an explicit lead-in segment from beat 0 when a span
 * starts at or before its first key (`sliceBetween`), so the region before the span keeps its sum as well (exact
 * for a straight first segment, otherwise noted). Linked segments keep their link groups (same values and curve).
 * On a line with position X/Y linked, the other axis swaps the same spans, so both axes keep their keys at the same
 * beats.
 */
export function moveSegmentsToLayerBody(tx: TxBuilder, segments: readonly KeyId[], layer: number): void {
  if (!Number.isSafeInteger(layer) || layer < 0) tx.fail(reason(MOTION_MSG.layerInvalid));
  let sawLayered = false;
  const jobs = new Map<string, SwapJob>();
  const jobFor = (line: LineId, prop: LineProp, from: number): SwapJob => {
    const key = `${String(line)}:${prop}:${String(from)}`;
    let job = jobs.get(key);
    if (job === undefined) {
      job = { line, prop, from, spans: [], selected: 0 };
      jobs.set(key, job);
    }
    return job;
  };
  for (const [trackId, ids] of groupByTrack(tx, segments)) {
    const info = requireTrack(tx, trackId);
    if (info.prop === null || info.line === null || !PROP_SPECS[info.prop].layered) continue;
    sawLayered = true;
    if (info.layer === layer) continue;
    const keys = readKeys(tx, trackId);
    const runs = segmentRuns(keys, new Set(ids.filter((id) => startsSegment(tx, id))));
    if (runs.length === 0) continue;
    const job = jobFor(info.line.id, info.prop, info.layer);
    for (const [a, b] of runs) {
      job.spans.push([(keys[a] as KeyRec).t, (keys[b] as KeyRec).t]);
      job.selected += b - a;
    }
  }
  if (!sawLayered) tx.fail(reason(MOTION_MSG.moveNotLayered));
  // X/Y link: the other axis of the same layer swaps the same spans (the union of both axes' spans).
  for (const job of [...jobs.values()]) {
    if (job.prop !== 'moveX' && job.prop !== 'moveY') continue;
    if (tx.doc.lines.get(job.line)?.editor.xyLinked !== true) continue;
    const partner = jobFor(job.line, job.prop === 'moveX' ? 'moveY' : 'moveX', job.from);
    partner.spans = unionSpans([...partner.spans, ...job.spans]);
    job.spans = partner.spans;
  }
  let moved = 0;
  for (const job of jobs.values()) moved += job.selected;
  if (moved === 0) tx.fail(reason(MOTION_MSG.nothingToMove));

  const unlinked = new Set<number>();
  let splits: SplitOn | null = null;
  let focus: Beat | null = null;
  for (const job of jobs.values()) {
    const src = layerWork(tx, job.line, job.prop, job.from);
    const dst = layerWork(tx, job.line, job.prop, layer);
    if (src === null || dst === null) continue;
    if (!src.exists && !dst.exists) continue;
    const srcCtx = curveCtx(tx, src.info);
    const dstCtx = curveCtx(tx, dst.info);
    for (const [s, e] of unionSpans(job.spans)) {
      const fromSrc = sliceBetween(src.keys, s, e, srcCtx, unlinked);
      const fromDst = sliceBetween(dst.keys, s, e, dstCtx, unlinked);
      splits = collectSplit(splits, src.info, fromSrc.report);
      splits = collectSplit(splits, dst.info, fromDst.report);
      // The edges already are keys of both layers: these replacements split nothing.
      replaceSpan(src.keys, fromDst.slice, srcCtx, unlinked);
      replaceSpan(dst.keys, fromSrc.slice, dstCtx, unlinked);
      if (focus === null || Beats.lt(s, focus)) focus = s;
    }
    commitLayer(tx, src);
    commitLayer(tx, dst);
  }
  cleanLinks(tx, unlinked);
  noteWorstSplit(tx, splits);
  if (focus !== null) tx.setFocus({ beat: focus });
}
